<div class="space-y-6">
  <app-page-header
    title="Commission globale"
    subtitle="Taux par défaut appliqués à toutes les locations. Les opérateurs peuvent surcharger via leur override individuel."
  />

  @if (error() || loading()) {
    <app-data-state
      [loading]="loading()"
      [error]="error()"
      loadingMessage="Chargement…"
      (retry)="fetchConfig()"
    />
  } @else if (config(); as c) {
    <!-- Current rates card -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Taux en vigueur</h2>
        <button
          type="button"
          (click)="openEdit()"
          class="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
        >
          Modifier les taux
        </button>
      </div>
      <div class="mt-5 grid gap-3 sm:grid-cols-3">
        <div class="rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800">
          <p class="text-xs text-gray-500 dark:text-gray-400">Commission totale</p>
          <p class="mt-1 text-2xl font-bold text-gray-800 dark:text-white/90">
            {{ c.defaultTotalPct | number:'1.2-2' }}<span class="ml-1 text-sm font-normal text-gray-400">%</span>
          </p>
          <p class="mt-0.5 text-xs text-gray-400">Prélevée sur le total de la location</p>
        </div>
        <div class="rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800">
          <p class="text-xs text-gray-500 dark:text-gray-400">Part plateforme</p>
          <p class="mt-1 text-2xl font-bold text-brand-700 dark:text-brand-400">
            {{ c.defaultPlatformPct | number:'1.2-2' }}<span class="ml-1 text-sm font-normal text-gray-400">%</span>
          </p>
          <p class="mt-0.5 text-xs text-gray-400">Vers le wallet plateforme</p>
        </div>
        <div class="rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800">
          <p class="text-xs text-gray-500 dark:text-gray-400">Part opérateur</p>
          <p class="mt-1 text-2xl font-bold text-blue-light-700 dark:text-blue-light-400">
            {{ c.defaultOperatorPct | number:'1.2-2' }}<span class="ml-1 text-sm font-normal text-gray-400">%</span>
          </p>
          <p class="mt-0.5 text-xs text-gray-400">Vers le wallet de l'opérateur résolu</p>
        </div>
      </div>
      @if (c.updatedAt) {
        <p class="mt-4 text-xs text-gray-500 dark:text-gray-400">
          Dernière modification : {{ c.updatedAt | ldate:'dd/MM/yyyy HH:mm' }}
        </p>
      }
    </section>

    <!-- History -->
    <section class="rounded-2xl border border-gray-200 bg-white shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-800">
        <h2 class="text-base font-semibold text-gray-800 dark:text-white/90">Historique des modifications</h2>
        <p class="text-xs text-gray-500 dark:text-gray-400">{{ historyTotalItems() }} entrée(s)</p>
      </div>

      @if (historyError() || (historyLoading() && history().length === 0) || history().length === 0) {
        <app-data-state
          [loading]="historyLoading()"
          [error]="historyError()"
          [empty]="!historyLoading() && !historyError() && history().length === 0"
          loadingMessage="Chargement de l'historique…"
          emptyMessage="Aucune modification enregistrée pour l'instant."
          (retry)="loadHistory()"
        />
      } @else {
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
            <thead class="bg-gray-50 dark:bg-white/[0.02]">
              <tr>
                <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Date</th>
                <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Portée</th>
                <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Cible</th>
                <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Avant (T / P / O)</th>
                <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Après (T / P / O)</th>
                <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Admin</th>
                <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Raison</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
              @for (h of history(); track h.id) {
                <tr class="transition hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                  <td class="whitespace-nowrap px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                    {{ h.createdAt | ldate:'dd/MM/yy HH:mm' }}
                  </td>
                  <td class="whitespace-nowrap px-5 py-4">
                    <span
                      class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                      [class.bg-brand-50]="h.scope === 'GLOBAL'"
                      [class.text-brand-700]="h.scope === 'GLOBAL'"
                      [class.bg-blue-light-50]="h.scope === 'OPERATOR_OVERRIDE'"
                      [class.text-blue-light-700]="h.scope === 'OPERATOR_OVERRIDE'"
                    >
                      {{ scopeLabel[h.scope] }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                    @if (h.targetOperatorId) {
                      <a [routerLink]="['/operators', h.targetOperatorId]" class="text-brand-600 hover:text-brand-700 dark:text-brand-400">
                        Opérateur #{{ h.targetOperatorId }}
                      </a>
                    } @else {
                      <span class="text-gray-400">—</span>
                    }
                  </td>
                  <td class="whitespace-nowrap px-5 py-4 font-mono text-xs text-gray-700 dark:text-gray-300">
                    {{ (h.oldTotalPct ?? '—') }} / {{ (h.oldPlatformPct ?? '—') }} / {{ (h.oldOperatorPct ?? '—') }}
                  </td>
                  <td class="whitespace-nowrap px-5 py-4 font-mono text-xs font-medium text-brand-700 dark:text-brand-400">
                    {{ (h.newTotalPct ?? '—') }} / {{ (h.newPlatformPct ?? '—') }} / {{ (h.newOperatorPct ?? '—') }}
                  </td>
                  <td class="whitespace-nowrap px-5 py-4 text-sm text-gray-500 dark:text-gray-400">
                    @if (h.adminUserId) {
                      <a [routerLink]="['/users', h.adminUserId]" class="text-brand-600 hover:text-brand-700 dark:text-brand-400">#{{ h.adminUserId }}</a>
                    } @else { — }
                  </td>
                  <td class="px-5 py-4 text-xs text-gray-600 dark:text-gray-400">{{ h.reason || '—' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        @if (historyTotalPages() > 0) {
          <div class="flex flex-col gap-3 border-t border-gray-200 px-5 py-4 dark:border-gray-800 sm:flex-row sm:items-center sm:justify-between">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Page {{ historyPage() }} sur {{ historyTotalPages() }}
            </p>
            <div class="flex flex-wrap items-center gap-1">
              <button type="button" (click)="goToHistoryPage(historyPage() - 1)" [disabled]="historyPage() <= 1" class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/5">Précédent</button>
              @for (n of historyPageWindow(); track n) {
                <button
                  type="button"
                  (click)="goToHistoryPage(n)"
                  class="min-w-[36px] rounded-lg border px-3 py-1.5 text-xs font-medium transition"
                  [class.border-brand-500]="n === historyPage()"
                  [class.bg-brand-500]="n === historyPage()"
                  [class.text-white]="n === historyPage()"
                  [class.border-gray-300]="n !== historyPage()"
                  [class.text-gray-700]="n !== historyPage()"
                  [class.hover:bg-gray-50]="n !== historyPage()"
                  [class.dark:border-gray-700]="n !== historyPage()"
                  [class.dark:text-gray-400]="n !== historyPage()"
                  [class.dark:hover:bg-white/5]="n !== historyPage()"
                >
                  {{ n }}
                </button>
              }
              <button type="button" (click)="goToHistoryPage(historyPage() + 1)" [disabled]="historyPage() >= historyTotalPages()" class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/5">Suivant</button>
            </div>
          </div>
        }
      }
    </section>
  }
</div>

<!-- Edit modal -->
<app-modal [isOpen]="editOpen()" (close)="closeEdit()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">Modifier les taux globaux</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    Contrainte : <strong>Total = Plateforme + Opérateur</strong>. Les opérateurs avec override individuel ne sont pas impactés.
  </p>

  @if (editError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ editError() }}
    </div>
  }

  <form [formGroup]="editForm" (ngSubmit)="submitEdit()" class="mt-5 space-y-4">
    <div class="grid grid-cols-3 gap-3">
      <div>
        <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">Total %</label>
        <input type="number" step="0.01" min="0" formControlName="defaultTotalPct"
          class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
      </div>
      <div>
        <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">Plateforme %</label>
        <input type="number" step="0.01" min="0" formControlName="defaultPlatformPct"
          class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
      </div>
      <div>
        <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">Opérateur %</label>
        <input type="number" step="0.01" min="0" formControlName="defaultOperatorPct"
          class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
      </div>
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeEdit()" [disabled]="editSaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      <button type="submit" [disabled]="editSaving() || editForm.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
        @if (editSaving()) { Enregistrement… } @else { Enregistrer }
      </button>
    </div>
  </form>
</app-modal>
